<script setup lang="ts">
import { useSettings } from '@base/composables/settings'
import { ut_wilo_filteredCountryData, ut_wilo_filteredLangData, ut_wilo_getFrequency, ut_wilo_modifyCountry } from '~/utils/utils'
import { ut_getText, ut_isEmbedded, ut_setLocalStorage } from '@base/utils/utils'
import type { ISelectItem } from '@base/utils/types'
import { EUserUserDefined } from '~/utils/constants'
import { ElocalStorageType } from '@base/utils/constants'

const p = defineProps<{
  isHistoric: boolean
  realCountry: string
  isInternal: boolean
  showFrequency: boolean
  /*
   * Leaves out everything that changes what a cart CONTAINS — country, frequency, date: each of them
   * restricts which series exist, and the cart would drop the ones that no longer do. Language stays,
   * it only changes how the contents read. Set on an open price-list project (see Cart.vue).
   */
  hideCartChanging?: boolean
}>()

const cp = useSettings()
const inputDate = shallowRef('')

onMounted(() => {
  if (!cp.rcre1Value.value) {
    cp.rcre1Value.value = ut_wilo_getFrequency()
  }
  if (cp.dateValue.value) {
    inputDate.value = cp.dateValue.value['start'].toString()
  }
})

const modalOpen: Ref<boolean> = ref(false)
const priceList: Ref<boolean> = ref(cp.indexStore.state.userDefined[EUserUserDefined.user_us_priceList])
const settingsText: string = ut_getText('txt_settings')

const buttonText = computed((): string => {
  const resAr: string[] = []
  if (p.isInternal) { resAr.push(cp.countryValue.value.toUpperCase()) }
  resAr.push(cp.langValue.value.toUpperCase())
  if (p.showFrequency && cp.rcre1Data.value) {
    const selectedHz: ISelectItem | undefined = cp.rcre1Data.value.find(v => v.value === cp.rcre1Value.value)
    resAr.push(selectedHz?.label || '')
  }
  if (cp.indexStore.state.userDefined[EUserUserDefined.user_us_priceList]) {
    resAr.push('$')
  }
  return resAr.join(' / ')
})

// Embedded, only internal users get the "Price list" switch (the allowlist restriction is gone since 15.09.2026).
const showAdditional = computed((): boolean => !ut_isEmbedded() || p.isInternal)

function setDate(): void {
  cp.changeDate({ start: Date.parse(inputDate.value), end: Date.parse(inputDate.value) })
}

function setPriceList(): void {
  cp.indexStore.setUserDefined(EUserUserDefined.user_us_priceList, priceList.value)
  ut_setLocalStorage(ElocalStorageType.ls_userinfo, JSON.stringify(cp.indexStore.state.userDefined))
}
</script>

<template>
  <UTooltip :text="settingsText">
    <UButton
      variant="shadow"
      color="tertiary"
      icon="i-wilo-sliders-regular-full"
      :aria-label="settingsText"
      :label="buttonText"
      @click="void (modalOpen = !modalOpen)"
    />
  </UTooltip>
  <UModal
    v-model:open="modalOpen"
    :title="settingsText"
    :ui="useAppConfig().modalFrame.auto"
  >
    <template #body>
      <!--
        No wrapping from `md` up, and that is not cosmetics: the modal sizes itself with `w-fit`
        (modalFrame.auto), so its width comes from the content's max-content width — and a WRAPPING
        flex container answers that question differently from how it then lays itself out. Measured
        on the internal page, 24.08.2026: five columns need 965px plus 4 x 64px gaps = 1221px, the
        container offered 1207px, and the last column dropped to the next line while the frame was
        allowed to be 2478px wide. It was 14px short of a decision it had already made.

        `flex-nowrap` removes the disagreement — with one line the max-content width is simply the
        sum, and the frame grows to it. Below `md` the wrap stays, where it is actually needed.
      -->
      <div class="flex flex-wrap md:flex-nowrap gap-x-16 gap-y-8">
        <!-- Country -->
        <div v-if="isInternal && !hideCartChanging">
          <div class="font-bold p-1 mb-6" v-html="ut_getText('label_lcc')" />
          <ul class="pr-2">
            <li v-for="item in ut_wilo_filteredCountryData(cp.countryData.value)" :key="item.value" class="last:pb-6">
              <div
                class="cursor-pointer p-1 hover:bg-gunmetal-3"
                :class="cp.countryValue.value === item.value ? 'font-bold text-primary' : '' "
                @click="cp.changeLangCountry('rcco', ut_wilo_modifyCountry(item.value, isHistoric));modalOpen = false"
                v-html="item.label"
              />
            </li>
          </ul>
        </div>
        <!-- Language -->
        <div>
          <div class="font-bold mb-6 p-1" v-html="ut_getText('label_lcd')" />
          <ul class="pr-2">
            <li v-for="item in ut_wilo_filteredLangData(cp.langData.value)" :key="item.value" class="last:pb-6">
              <div
                class="cursor-pointer p-1 hover:bg-gunmetal-3"
                :class="cp.langValue.value === item.value ? 'font-bold text-primary' : '' "
                @click="cp.changeLangCountry('lcda', item.value);modalOpen = false"
                v-html="item.label"
              />
            </li>
          </ul>
        </div>
        <!-- Frequency -->
        <div v-if="showFrequency && !hideCartChanging">
          <div class="font-bold p-1 mb-6" v-html="ut_getText('label_unit_hz')" />
          <ul class="pr-2">
            <li v-for="item in cp.rcre1Data.value" :key="item.value">
              <div
                v-if="item.value !== '-'"
                class="cursor-pointer p-1 hover:bg-gunmetal-3"
                :class="cp.rcre1Value.value === item.value ? 'font-bold text-primary' : '' "
                @click="cp.changeRest('rcre1', item.value);modalOpen = false"
                v-html="item.label"
              />
            </li>
          </ul>
        </div>
        <!-- Date -->
        <div v-if="(isInternal || !ut_isEmbedded()) && !hideCartChanging">
          <div class="font-bold p-1 mb-6" v-html="ut_getText('label_date')" />
          <UInput
            v-model="inputDate"
            type="date"
            @update:model-value="setDate()"
          />
        </div>
        <!-- Additional -->
        <div v-if="showAdditional">
          <div class="font-bold p-1 mb-6" v-html="'Additional features'" />
          <UCheckbox v-model="priceList" label="Price list" @change="setPriceList" />
        </div>
      </div>
    </template>
  </UModal>

<!--  <div class="flex flex-wrap gap-4">
    <UPopover v-if="isInternal">
      <UTooltip :text="ut_getText('label_lcc')">
        <UButton color="tertiary" variant="ghost" icon="i-wilo-globe-solid-full" :label="realCountry.toUpperCase()" />
      </UTooltip>
      <template #content>
        <ul class="overflow-auto  max-h-100 lg:max-h-200 p-4">
          <li v-for="item in ut_wilo_filteredCountryData(cp.countryData.value)" :key="item.value">
            <div
              class="cursor-pointer p-1 hover:bg-gunmetal-3"
              :class="cp.countryValue.value === item.value ? 'font-bold' : '' "
              @click="cp.changeLangCountry('rcco', ut_wilo_modifyCountry(item.value, isHistoric))"
              v-html="item.label"
            />
          </li>
        </ul>
      </template>
    </UPopover>
    <UPopover>
      <UTooltip :text="ut_getText('label_lcd')">
        <UButton color="tertiary" variant="ghost" icon="i-wilo-language-solid-full" :label="cp.langValue.value.toUpperCase()" />
      </UTooltip>
      <template #content>
        <ul class="overflow-auto max-h-100 lg:max-h-200 p-4">
          <li v-for="item in ut_wilo_filteredLangData(cp.langData.value)" :key="item.value">
            <div
              class="cursor-pointer p-1 hover:bg-gunmetal-3"
              :class="cp.langValue.value === item.value ? 'font-bold' : '' "
              @click="cp.changeLangCountry('lcda', item.value)"
              v-html="item.label"
            />
          </li>
        </ul>
      </template>
    </UPopover>

    <UPopover v-if="showFrequency">
      <UTooltip :text="ut_getText('label_unit_hz')">
        <UButton color="tertiary" variant="ghost" icon="i-wilo-wave-square-solid-full" :label="cp.getSelectItemLabel(cp.rcre1Data.value || [], cp.rcre1Value.value)" />
      </UTooltip>
      <template #content>
        <ul class="overflow-auto  max-h-100 lg:max-h-200 p-4">
          <li v-for="item in cp.rcre1Data.value" :key="item.value">
            <div
              v-if="item.value !== '-'"
              class="cursor-pointer p-1 hover:bg-gunmetal-3"
              :class="cp.rcre1Value.value === item.value ? 'font-bold' : '' "
              @click="cp.changeRest('rcre1', item.value)"
              v-html="item.label"
            />
          </li>
        </ul>
      </template>
    </UPopover>

    <UTooltip :text="ut_getText('label_date')">
      <UInput
        v-if="isInternal"
        v-model="inputDate"
        type="date"
        @update:model-value="setDate()"
      />
    </UTooltip>
  </div> -->
</template>
